import React, { useMemo, useState } from 'react'
import {
  Typography,
  Paper,
  Box,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
} from '@mui/material'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import _sortBy from 'lodash/sortBy'

const ACCENT = '#00B2B2'

// "Marathon - Indonesian (Group)" -> "Marathon", "Marathon 42K - Charity Run" -> "Marathon",
// "300m Children Sprint (6-8 years)" -> "300m Children Sprint", "10K - Foreigner" -> "10K"
const getGroupName = (ticketTypeName) => {
  const name = (ticketTypeName || '').trim()
  if (!name) return 'Other'
  const head = name.split(' - ')[0]
  const trimmed = head
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/\s*\[[^\]]*\]\s*$/, '')
    .trim()
  // Drop a trailing distance token ("Marathon 42K" -> "Marathon"), unless it is the whole name.
  const withoutDistance = trimmed.replace(/\s+\d+(\.\d+)?\s*(K|KM|M)$/i, '').trim()
  return withoutDistance || trimmed || name
}

const percentOf = (count, stock) => (stock > 0 ? (count / stock) * 100 : 0)

const ProgressBar = ({ value }) => (
  <LinearProgress
    variant="determinate"
    value={Math.min(value, 100)}
    sx={{
      height: { xs: 4, sm: 5, md: 6 },
      borderRadius: 1,
      backgroundColor: (theme) =>
        theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
      '& .MuiLinearProgress-bar': {
        borderRadius: 1,
        backgroundColor: ACCENT,
      },
    }}
  />
)

const StatsTicketTypes = ({ ticketTypes }) => {
  const [expanded, setExpanded] = useState({})

  const groups = useMemo(() => {
    const byName = {}
    ;(ticketTypes || []).forEach((ticketType) => {
      const groupName = getGroupName(ticketType._id)
      if (!byName[groupName]) {
        byName[groupName] = { name: groupName, count: 0, stock: 0, items: [] }
      }
      byName[groupName].count += ticketType.count || 0
      byName[groupName].stock += ticketType.stock || 0
      byName[groupName].items.push(ticketType)
    })
    return _sortBy(Object.values(byName), (group) => -group.count).map((group) => ({
      ...group,
      items: _sortBy(group.items, (item) => -(item.count || 0)),
    }))
  }, [ticketTypes])

  if (!ticketTypes || ticketTypes.length === 0) {
    return null
  }

  const total = ticketTypes.reduce((prev, cur) => prev + (cur.count || 0), 0)
  const totalStock = ticketTypes.reduce((prev, cur) => prev + (cur.stock || 0), 0)
  const expandableGroups = groups.filter((group) => group.items.length > 1)
  const allExpanded =
    expandableGroups.length > 0 && expandableGroups.every((group) => expanded[group.name])

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded({})
      return
    }
    const next = {}
    expandableGroups.forEach((group) => {
      next[group.name] = true
    })
    setExpanded(next)
  }

  const toggleGroup = (groupName) => {
    setExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }))
  }

  const numericCellSx = { fontSize: { xs: '0.8125rem', sm: '0.875rem' } }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2.5, md: 3 },
        borderRadius: 2,
        mb: { xs: 2, sm: 2.5, md: 3 },
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 1)' : 'rgba(18, 18, 18, 1)',
        border: (theme) =>
          `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        boxShadow: (theme) =>
          theme.palette.mode === 'light'
            ? '0 2px 8px rgba(0, 0, 0, 0.08)'
            : '0 2px 8px rgba(0, 0, 0, 0.3)',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: { xs: 1.5, sm: 2.5, md: 3 },
        }}>
        <ConfirmationNumberIcon sx={{ color: ACCENT }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
          }}>
          Ticket Types
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              fontWeight: 500,
            }}>
            Total {total.toLocaleString()} / {totalStock.toLocaleString()}
          </Typography>
          {expandableGroups.length > 0 && (
            <Button
              size="small"
              onClick={toggleAll}
              sx={{ color: ACCENT, textTransform: 'none', fontWeight: 600 }}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Ticket Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Count
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Stock
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                %
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '30%' }}>
                Progress
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const isExpandable = group.items.length > 1
              const isOpen = !!expanded[group.name]
              // A one-ticket group has nothing to expand into — show the ticket itself.
              const label = isExpandable ? group.name : group.items[0]._id || group.name

              return (
                <React.Fragment key={group.name}>
                  <TableRow
                    hover={isExpandable}
                    onClick={isExpandable ? () => toggleGroup(group.name) : undefined}
                    sx={{
                      cursor: isExpandable ? 'pointer' : 'default',
                      backgroundColor: (theme) =>
                        isExpandable && theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'transparent',
                    }}>
                    <TableCell sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isExpandable ? (
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            {isOpen ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 24 }} />
                        )}
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: isExpandable ? 700 : 400,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          }}>
                          {label}
                        </Typography>
                        {isExpandable && (
                          <Typography
                            component="span"
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            }}>
                            ({group.items.length})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...numericCellSx, fontWeight: 700, color: 'text.primary' }}>
                      {group.count.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numericCellSx, color: 'text.secondary' }}>
                      {group.stock ? group.stock.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numericCellSx, color: 'text.secondary' }}>
                      {group.stock > 0 ? `${percentOf(group.count, group.stock).toFixed(1)}%` : '-'}
                    </TableCell>
                    <TableCell>
                      <ProgressBar value={percentOf(group.count, group.stock)} />
                    </TableCell>
                  </TableRow>

                  {isExpandable &&
                    isOpen &&
                    group.items.map((item) => {
                      const count = item.count || 0
                      const stock = item.stock || 0
                      return (
                        <TableRow key={item._id}>
                          <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', pl: 5 }}>
                            <Typography
                              component="span"
                              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                              {item._id || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...numericCellSx, fontWeight: 600, color: 'text.primary' }}>
                            {count.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...numericCellSx, color: 'text.secondary' }}>
                            {stock ? stock.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...numericCellSx, color: 'text.secondary' }}>
                            {stock > 0 ? `${percentOf(count, stock).toFixed(1)}%` : '-'}
                          </TableCell>
                          <TableCell>
                            <ProgressBar value={percentOf(count, stock)} />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  )
}

export default StatsTicketTypes
